import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiFilter, FiX, FiCheck, FiChevronDown, FiSearch, FiTag } = FiIcons

const FilterPanel = ({ filters, onFilterChange, onClearFilters, isOpen, onToggle, categories = [] }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const handleCategoryToggle = (category) => {
    const currentCategories = Array.isArray(filters.category) ? filters.category : [];
    const newCategories = currentCategories.includes(category)
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];
    
    onFilterChange({
      ...filters,
      category: newCategories
    });
  }

  const handleAvailabilityChange = (available) => {
    onFilterChange({
      ...filters,
      available: filters.available === available ? false : available
    })
  }

  const filteredCategories = categories.filter(cat => 
    cat.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (!isOpen) {
    return (
      <motion.button
        whileHover={{ scale: 1.1, rotate: 5 }}
        whileTap={{ scale: 0.9 }}
        onClick={onToggle}
        className="fixed right-6 top-1/2 transform -translate-y-1/2 bg-dark_cyan text-white p-4 rounded-full shadow-2xl hover:bg-dark_cyan-400 transition-all z-40 border-2 border-white"
      >
        <SafeIcon icon={FiFilter} className="w-6 h-6" />
      </motion.button>
    )
  }

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="fixed right-0 top-0 h-full w-80 bg-white shadow-2xl border-l-2 border-white-400 z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b-2 border-white-400 bg-space_indigo-900">
        <div>
          <h3 className="text-lg font-black text-space_indigo flex items-center uppercase tracking-tight">
            <SafeIcon icon={FiFilter} className="w-5 h-5 mr-3 text-dark_cyan" /> 
            Refine Search
          </h3>
          <p className="text-[9px] font-black text-white-300 uppercase tracking-widest mt-1">Adjust your view</p>
        </div>
        <button 
          onClick={onToggle}
          className="p-2 bg-white-500 border-2 border-white-400 rounded-xl text-white-300 hover:text-hot_berry transition-all"
        >
          <SafeIcon icon={FiX} className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
        {/* Availability Toggle */}
        <section>
          <h4 className="text-[10px] font-black text-white-300 uppercase tracking-[0.2em] mb-4 flex items-center">
            Status
          </h4>
          <button
            onClick={() => handleAvailabilityChange(true)}
            className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all ${
              filters.available 
                ? 'bg-dark_cyan-900 border-dark_cyan text-dark_cyan' 
                : 'bg-white-500 border-white-400 text-white-200 hover:border-white-300'
            }`}
          >
            <span className="text-xs font-black uppercase tracking-widest">Available Now</span>
            <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-colors ${
              filters.available ? 'bg-dark_cyan border-dark_cyan text-white' : 'bg-white border-white-400'
            }`}>
              {filters.available && <SafeIcon icon={FiCheck} className="w-3 h-3" />}
            </div>
          </button>
        </section>

        {/* Categories Dropdown Multiselect */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-[10px] font-black text-white-300 uppercase tracking-[0.2em]">Categories</h4>
            {filters.category?.length > 0 && (
              <span className="bg-hot_berry text-white px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest">
                {filters.category.length} Active
              </span>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`w-full flex items-center justify-between p-4 bg-white-500 border-2 rounded-2xl transition-all ${
                isDropdownOpen ? 'border-dark_cyan ring-4 ring-dark_cyan/10' : 'border-white-400 hover:border-white-300'
              }`}
            >
              <div className="flex items-center truncate mr-2">
                <SafeIcon icon={FiTag} className="w-4 h-4 mr-3 text-dark_cyan shrink-0" />
                <span className="text-xs font-bold text-space_indigo truncate">
                  {filters.category?.length > 0 
                    ? filters.category.join(', ') 
                    : 'Select Categories'}
                </span>
              </div>
              <SafeIcon 
                icon={FiChevronDown} 
                className={`w-4 h-4 text-white-300 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} 
              />
            </button>

            <AnimatePresence>
              {isDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-white-400 rounded-2xl shadow-2xl z-20 overflow-hidden flex flex-col max-h-[320px]"
                >
                  <div className="p-3 border-b-2 border-white-400 bg-white-500 flex items-center">
                    <SafeIcon icon={FiSearch} className="w-3.5 h-3.5 text-white-300 mr-2" />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Filter categories..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full bg-transparent border-none focus:ring-0 text-[11px] font-bold text-space_indigo placeholder-white-300 p-0"
                    />
                  </div>
                  
                  <div className="overflow-y-auto custom-scrollbar flex-1">
                    {filteredCategories.length === 0 ? (
                      <div className="p-6 text-center">
                        <p className="text-[10px] font-black text-white-300 uppercase italic">No matches found</p>
                      </div>
                    ) : (
                      filteredCategories.map(category => {
                        const isSelected = filters.category?.includes(category);
                        return (
                          <button
                            key={category}
                            onClick={() => handleCategoryToggle(category)}
                            className={`w-full flex items-center p-3.5 text-left transition-all hover:bg-white-500 group ${
                              isSelected ? 'bg-dark_cyan/5' : ''
                            }`}
                          >
                            <div className={`w-4 h-4 rounded-md border-2 shrink-0 flex items-center justify-center transition-all ${
                              isSelected 
                                ? 'bg-dark_cyan border-dark_cyan text-white' 
                                : 'bg-white border-white-400 group-hover:border-dark_cyan-400'
                            }`}>
                              {isSelected && <SafeIcon icon={FiCheck} className="w-2.5 h-2.5" />}
                            </div>
                            <span className={`ml-3 text-[11px] tracking-wide transition-colors ${
                              isSelected ? 'text-dark_cyan font-black' : 'text-white-100 font-bold'
                            }`}>
                              {category}
                            </span>
                          </button>
                        )
                      })
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      </div>

      {/* Footer */}
      <div className="p-6 border-t-2 border-white-400 bg-white-500">
        <button
          onClick={() => {
            onClearFilters();
            setIsDropdownOpen(false);
          }}
          disabled={!filters.available && (!filters.category || filters.category.length === 0)}
          className="w-full py-4 bg-space_indigo text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl shadow-space_indigo/20 hover:bg-space_indigo-400 disabled:opacity-50 disabled:bg-white-400 disabled:shadow-none transition-all"
        >
          Reset All Filters
        </button>
      </div>
    </motion.div>
  )
}

export default FilterPanel